/**
 * Caps responsive-image selection at 2x device pixels on touch devices.
 *
 * On DPR 3 phones a `w`-descriptor srcset selects ~2.25x the pixels of 2x, for detail
 * few shoppers can see. Scaling each `sizes` length by 2/dpr makes the browser pick
 * the candidate it would at 2x. Zoom (`x-lightbox`) loads its own sources, and anything
 * inside `[data-full-density]` (the PDP gallery) is left at native density.
 */
const MAX_DENSITY = 2

/** Desktop zoom and OS scaling also raise devicePixelRatio; only phones/tablets are capped. */
function densityScale(dpr: number): number {
  if (dpr <= MAX_DENSITY || !window.matchMedia('(pointer: coarse)').matches) return 1
  // Rounded down: 0.6667 would land a hair above 2x and skip an exact-2x ladder entry.
  return Math.floor((MAX_DENSITY / dpr) * 1000) / 1000
}

/** Splits on commas/whitespace that sit outside parentheses. */
function splitTopLevel(value: string, separator: RegExp): string[] {
  const parts: string[] = []
  let depth = 0
  let current = ''
  for (const char of value) {
    if (char === '(') depth++
    if (char === ')') depth--
    if (depth === 0 && separator.test(char)) {
      if (current.trim()) parts.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  if (current.trim()) parts.push(current.trim())
  return parts
}

export function capSizes(sizes: string, dpr = window.devicePixelRatio || 1): string {
  const scale = densityScale(dpr)
  if (!sizes || scale === 1) return sizes

  return splitTopLevel(sizes, /,/)
    .map((entry) => {
      const tokens = splitTopLevel(entry, /\s/)
      const length = tokens.pop()
      if (!length || length === 'auto') return entry
      return [...tokens, `calc(${length} * ${scale})`].join(' ')
    })
    .join(', ')
}

/**
 * Caps `data-sizes` once (marked `data-sizes-capped`), including `<source>` siblings in a
 * `<picture>`. Used as vanilla-lazyload's `callback_enter`, which runs before it copies
 * data-sizes onto the element, and by `loadDeferredImages`.
 */
export function capLazySizes(el: HTMLElement): void {
  if (el.closest('[data-full-density]')) return
  const targets = [el, ...Array.from(el.parentElement?.tagName === 'PICTURE' ? el.parentElement.querySelectorAll('source') : [])]
  targets.forEach((target) => {
    const { sizes, sizesCapped } = target.dataset
    if (!sizes || sizesCapped) return
    target.dataset.sizes = capSizes(sizes)
    target.dataset.sizesCapped = 'true'
  })
}
